
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Guide, GuideFormData } from "@/types/guides";

export function useGuides() {
  const queryClient = useQueryClient();

  const { data: guides = [], isLoading } = useQuery({
    queryKey: ['guides'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('guides')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Guide[];
    }
  });

  const createGuideMutation = useMutation({
    mutationFn: async (guide: Omit<Guide, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('guides')
        .insert([guide])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guides'] });
      toast.success('Guide created successfully');
    },
    onError: (error) => {
      console.error('Error creating guide:', error);
      toast.error('Failed to create guide');
    }
  });

  const updateGuideMutation = useMutation({
    mutationFn: async ({ id, ...guide }: Partial<Guide> & { id: string }) => {
      const { data, error } = await supabase
        .from('guides')
        .update(guide)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guides'] });
      toast.success('Guide updated successfully');
    },
    onError: (error) => {
      console.error('Error updating guide:', error);
      toast.error('Failed to update guide');
    }
  });

  const deleteGuideMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('guides')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guides'] });
      toast.success('Guide deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting guide:', error);
      toast.error('Failed to delete guide');
    }
  });

  return {
    guides,
    isLoading,
    createGuide: createGuideMutation.mutate,
    updateGuide: updateGuideMutation.mutate,
    deleteGuide: deleteGuideMutation.mutate,
  };
}

// Export the Guide type to make it available for other modules
export type { Guide, GuideFormData };
